import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Button,
  ConfigProvider,
  Empty,
  Segmented,
  Select,
  Skeleton,
  Space,
  Spin,
  message,
} from "antd";
import {
  ApartmentOutlined,
  FileExcelOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { ThreeMPhaseNode } from "../ThreeMConsolidation/threeMShared";
import { MACHINERY_HEADS } from "../../machinery-norms/machineryHeads";
import MachineryKpiCards from "./MachineryKpiCards";
import MachineryCostCharts from "./MachineryCostCharts";
import MachinerySummaryTable from "./MachinerySummaryTable";
import MachineryActivityTaskTable from "./MachineryActivityTaskTable";
import EquipmentDrillDrawer, {
  EquipmentDrillScope,
} from "./EquipmentDrillDrawer";
import PhaseCostDrawer from "./PhaseCostDrawer";
import MachineryTaskBreakdownDrawer from "./MachineryTaskBreakdownDrawer";
import MachineryMonthlyScheduleDrawer from "./MachineryMonthlyScheduleDrawer";
import WbsCodeToggle from "../../shared/WbsCodeToggle";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  MachinerySummaryData,
  MachinerySummaryRow,
  num,
} from "./machineryShared";

// Project-level machinery cost analysis: machinery items aggregated across
// the itemized sources — applied machinery norms ('Norm'), task tools
// ('Tools') and applied deployment norms ('Equipment') — plus the consumables
// bucket (tool split materials, in the total but never itemized). KPI cards +
// source donut + Pareto + unified register. Project context comes the same
// way as ManpowerCostAnalysis: location.state { id, projectName } with the
// optional :projectId fallback.

// Head filter values: "ALL" (omit the param) | a machinery head key |
// 'EQUIPMENT' (the synthetic head of the deployment-norm source). Mirrors the
// Machinery Norms master heads plus the Equipment arm. Tool items (head
// 'TOOLS' in the payload) are reachable via "All" but not offered as a filter.
//
// With a single option beyond "All" there is nothing to choose, so the
// control hides itself — see `showHeadFilter` below. The list used to carry
// nine heads, eight of which never had data; those eight turned out to be
// computed columns of the Equipment Rates master rather than heads, and were
// removed. 'EQUIPMENT' is NOT a MACHINERY_HEADS entry (that list is a write
// contract) — it is appended here by hand.
const HEAD_ALL = "ALL";
const HEAD_OPTIONS = [
  { label: "All", value: HEAD_ALL },
  ...MACHINERY_HEADS.map((h) => ({ label: h.label, value: h.key })),
  { label: "Equipment", value: "EQUIPMENT" },
];
const showHeadFilter = HEAD_OPTIONS.length > 2;

const MachineryCostAnalysisReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const projectId = stateProjectId ?? params.projectId;

  const [data, setData] = useState<MachinerySummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const [activityId, setActivityId] = useState<number | undefined>(undefined);
  const [head, setHead] = useState<string>(HEAD_ALL);

  // Phase/Activity dropdown options come from the unfiltered 3M consolidation
  // tree (same source the 3M report uses), so the machinery summary payload —
  // which carries no phase info — never has to.
  const [basePhases, setBasePhases] = useState<ThreeMPhaseNode[]>([]);

  const [refreshTick, setRefreshTick] = useState(0);
  // Which grain the activity register shows. Defaults to "task": it is the
  // only grain under which equipment cost appears against a real activity.
  const [registerGrain, setRegisterGrain] = useState<"task" | "norm">("task");
  // Scope whose Equipment figure is being explained; null = drawer closed.
  const [equipmentScope, setEquipmentScope] =
    useState<EquipmentDrillScope | null>(null);
  const [exporting, setExporting] = useState(false);
  const [phaseDrawerOpen, setPhaseDrawerOpen] = useState(false);

  // Org branding for the Excel export. The currentUser slice stores the raw
  // /users/currentUser payload; orgName/orgLogo exist at runtime even though
  // the slice's local Data interface doesn't declare them.
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;

  // Export toasts go through a message instance whose holder lives inside the
  // report container — global message portals to document.body, which is
  // invisible while this container is fullscreen.
  const [msgApi, msgHolder] = message.useMessage();
  // Per-item task drill-down (phase → activity → task); null = closed.
  // Per-item month-by-month schedule; mutually exclusive with the task
  // drill-down (antd stacks drawers at one z-index).
  const [scheduleRow, setScheduleRow] = useState<MachinerySummaryRow | null>(
    null,
  );
  const [breakdownRow, setBreakdownRow] = useState<MachinerySummaryRow | null>(
    null
  );

  // Fullscreen: the report container goes fullscreen via the browser API.
  // Popups (Select dropdowns, tooltips) and the drawers are re-targeted INTO
  // the container — portals left on document.body would be invisible while
  // another element is fullscreen. Same pattern as ThreeMConsolidation.
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    // Covers both the button and ESC (the browser exits without telling us).
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  // Project top-menu, same as WBSViewNew / ThreeMConsolidation
  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter options: one unfiltered consolidation load per project.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    threeMReportApis
      .get3MConsolidation(projectId)
      .then((response) => {
        if (!cancelled && response?.status) {
          setBasePhases(response?.data?.phases || []);
        }
      })
      .catch((err) => {
        // Non-fatal: the report still renders, only the filters stay empty.
        console.error("Error loading phase/activity filter options:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await threeMReportApis.get3MMachinerySummary(
          projectId,
          {
            phaseId,
            activityId,
            head: head === HEAD_ALL ? undefined : head,
          }
        );
        const { status, data: payload } = response || {};
        if (cancelled) return;
        if (status) {
          setData(payload || {});
        } else {
          setError(response?.message || "Failed to load machinery summary");
          message.error(
            response?.message || "Failed to load machinery summary"
          );
        }
      } catch (err) {
        console.error("Error loading machinery cost analysis:", err);
        if (!cancelled) {
          setError("Error loading Machinery Cost Analysis");
          message.error("Error loading Machinery Cost Analysis");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, phaseId, activityId, head, refreshTick]);

  const projectName = data?.projectName || stateProjectName || "Project";
  const items = useMemo(() => data?.items || [], [data]);

  const normCost = useMemo(() => {
    const apiNorm = num(data?.normCost);
    if (apiNorm > 0) return apiNorm;
    return items
      .filter((m) => m.source === "NORM")
      .reduce((sum, m) => sum + num(m.totalCost), 0);
  }, [data, items]);

  const toolsCost = useMemo(() => {
    const apiTools = num(data?.toolsCost);
    if (apiTools > 0) return apiTools;
    // Not `=== "TOOLS"`: an absent source still means Tools (matching
    // machineryRowKey's else branch), it just must never swallow EQUIPMENT.
    return items
      .filter((m) => m.source !== "NORM" && m.source !== "EQUIPMENT")
      .reduce((sum, m) => sum + num(m.totalCost), 0);
  }, [data, items]);

  // Deployment-norm (equipment) cost — the field and its rows only exist on
  // backends that fold the EQUIPMENT source in; both paths are 0 on older
  // backends, so the page renders exactly as before.
  const equipmentCost = useMemo(() => {
    const apiEquipment = num(data?.equipmentCost);
    if (apiEquipment > 0) return apiEquipment;
    return items
      .filter((m) => m.source === "EQUIPMENT")
      .reduce((sum, m) => sum + num(m.totalCost), 0);
  }, [data, items]);

  // Consumables (tool split materials) are a lump-sum bucket — no item rows,
  // so there is nothing to fall back to.
  const consumablesCost = useMemo(() => num(data?.consumablesCost), [data]);

  // Grand total for shares: trust the API value (= norm + tools + equipment +
  // consumables), fall back to the same sum (gRPC may omit zero fields).
  const grandTotal = useMemo(() => {
    const apiTotal = num(data?.totalCost);
    if (apiTotal > 0) return apiTotal;
    return normCost + toolsCost + equipmentCost + consumablesCost;
  }, [data, normCost, toolsCost, equipmentCost, consumablesCost]);

  const wbsName = useWbsName();
  const phaseOptions = useMemo(
    () =>
      basePhases.map((phase) => ({
        label: wbsName(phase.phaseName),
        value: phase.phaseId,
      })),
    [basePhases]
  );

  const activityOptions = useMemo(() => {
    if (!phaseId) return [];
    const phase = basePhases.find((p) => p.phaseId === phaseId);
    return (phase?.activities || []).map((activity) => ({
      label: wbsName(activity.activityName),
      value: activity.activityId,
    }));
  }, [basePhases, phaseId, wbsName]);

  const hasFilters = !!phaseId || !!activityId || head !== HEAD_ALL;

  const clearFilters = () => {
    setPhaseId(undefined);
    setActivityId(undefined);
    setHead(HEAD_ALL);
  };

  // Styled multi-sheet Excel export: fetches the all-items task breakdown for
  // the current filters, then hands everything to the lazily-loaded exceljs
  // builder. The logo fetch is best-effort (cross-origin blob).
  const handleExport = async () => {
    if (!projectId || !data) return;
    setExporting(true);
    try {
      const [breakdownResponse, monthResponse, helpers] = await Promise.all([
        threeMReportApis.get3MMachineryTaskBreakdownAll(projectId, {
          phaseId,
          activityId,
          head: head === HEAD_ALL ? undefined : head,
        }),
        threeMReportApis.get3MMachineryMonthlyScheduleAll(projectId, {
          phaseId,
          activityId,
          head: head === HEAD_ALL ? undefined : head,
        }),
        import("../shared/excelHelpers"),
      ]);
      // The month grid is additive: a project with no task schedules still
      // gets the other sheets rather than a failed export.
      if (!monthResponse?.status) {
        console.warn(
          "Month-wise sheet skipped:",
          monthResponse?.message || "monthly schedule fetch failed"
        );
      }
      if (!breakdownResponse?.status) {
        msgApi.error(breakdownResponse?.message || "Failed to load export data");
        return;
      }
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportWorkbook } = await import("./exportWorkbook");
      const phaseName = phaseId
        ? basePhases.find((p) => p.phaseId === phaseId)?.phaseName
        : undefined;
      const activityName =
        phaseId && activityId
          ? basePhases
              .find((p) => p.phaseId === phaseId)
              ?.activities?.find((a) => a.activityId === activityId)
              ?.activityName
          : undefined;
      const headLabel =
        head === HEAD_ALL
          ? undefined
          : HEAD_OPTIONS.find((o) => o.value === head)?.label || head;
      await exportWorkbook({
        data: { ...data, projectName },
        grandTotal,
        normCost,
        toolsCost,
        equipmentCost,
        consumablesCost,
        breakdownRows: breakdownResponse?.data?.rows || [],
        monthCells: monthResponse?.status ? monthResponse.data?.rows || [] : [],
        orgName,
        logo,
        filters: { phaseName, activityName, headLabel },
      });
      msgApi.success("Excel exported");
    } catch (err) {
      console.error("Error exporting Machinery Cost Analysis:", err);
      msgApi.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Head title="Machinery Cost Analysis" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
        <ConfigProvider getPopupContainer={popupContainer}>
        {msgHolder}
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  Machinery Cost Analysis
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · Machinery cost rollup across norms, task
                  tools and equipment
                </span>
              </div>
              <Space>
                <Button
                  icon={
                    isFullscreen ? (
                      <FullscreenExitOutlined />
                    ) : (
                      <FullscreenOutlined />
                    )
                  }
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </Button>
                <Button
                  icon={<ApartmentOutlined />}
                  onClick={() => setPhaseDrawerOpen(true)}
                  disabled={basePhases.length === 0}
                >
                  Phase Costs
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exporting}
                  disabled={loading || !data || items.length === 0}
                  onClick={handleExport}
                >
                  Export
                </Button>
                <WbsCodeToggle />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* Filter bar */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Phase"
                style={{ width: 220 }}
                options={phaseOptions}
                value={phaseId}
                onChange={(value) => {
                  setPhaseId(value);
                  setActivityId(undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Activity"
                style={{ width: 220 }}
                options={activityOptions}
                value={activityId}
                disabled={!phaseId}
                onChange={(value) => setActivityId(value)}
              />
              {/* Head filter: machinery heads (norm categories) + Equipment.
                  Hidden while "All" has only one alternative — the two would
                  be the same result set. */}
              {showHeadFilter ? (
                <Select
                  style={{ width: 180 }}
                  options={HEAD_OPTIONS}
                  value={head}
                  onChange={(value) => setHead(value)}
                />
              ) : null}
              {hasFilters ? (
                <Button type="link" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null}
            </div>

            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : error && !data ? (
              <Empty description={error} style={{ padding: "60px 0" }} />
            ) : (
              <Spin spinning={loading}>
                <MachineryKpiCards
                  data={data}
                  grandTotal={grandTotal}
                  normCost={normCost}
                  toolsCost={toolsCost}
                  equipmentCost={equipmentCost}
                  consumablesCost={consumablesCost}
                />
                {items.length === 0 ? (
                  <Empty
                    description={
                      hasFilters
                        ? "No machinery items match the current filters"
                        : "No machinery cost data for this project yet"
                    }
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  <>
                    <MachineryCostCharts
                      items={items}
                      grandTotal={grandTotal}
                      normCost={normCost}
                      toolsCost={toolsCost}
                      equipmentCost={equipmentCost}
                      consumablesCost={consumablesCost}
                    />
                    {/* Two grains of the SAME activity register. "By Task" is
                        the default because it is the only one that shows all
                        of machinery under an activity: equipment norms carry
                        no activity of their own, so in the by-norm view they
                        all fall into one "no activity" bucket. By-norm stays
                        one click away — it is where the per-item rates and
                        the drill-downs live. */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: 10,
                      }}
                    >
                      <Segmented
                        value={registerGrain}
                        onChange={(value) =>
                          setRegisterGrain(value as "task" | "norm")
                        }
                        options={[
                          { label: "By Task", value: "task" },
                          { label: "By Norm", value: "norm" },
                        ]}
                      />
                    </div>
                    {registerGrain === "task" ? (
                      <MachineryActivityTaskTable
                        projectId={projectId}
                        phaseId={phaseId}
                        activityId={activityId}
                        head={head === HEAD_ALL ? undefined : head}
                        refreshTick={refreshTick}
                        grandTotal={grandTotal}
                        onEquipmentClick={(scope) => {
                          setBreakdownRow(null);
                          setScheduleRow(null);
                          setEquipmentScope(scope);
                        }}
                      />
                    ) : (
                      <MachinerySummaryTable
                        projectId={projectId}
                        phaseId={phaseId}
                        activityId={activityId}
                        head={head === HEAD_ALL ? undefined : head}
                        refreshTick={refreshTick}
                        grandTotal={grandTotal}
                        onTasksClick={(row) => {
                          setScheduleRow(null);
                          setBreakdownRow(row);
                        }}
                        onItemClick={(row) => {
                          setBreakdownRow(null);
                          setScheduleRow(row);
                        }}
                      />
                    )}
                  </>
                )}
              </Spin>
            )}
          </div>
        </Card>

        <EquipmentDrillDrawer
          open={!!equipmentScope}
          projectId={projectId}
          phaseId={phaseId}
          scope={equipmentScope}
          onClose={() => setEquipmentScope(null)}
          getContainer={popupContainer}
        />

        <PhaseCostDrawer
          open={phaseDrawerOpen}
          phases={basePhases}
          onClose={() => setPhaseDrawerOpen(false)}
          getContainer={popupContainer}
        />

        <MachineryMonthlyScheduleDrawer
          open={!!scheduleRow}
          projectId={projectId}
          row={scheduleRow}
          phaseId={phaseId}
          activityId={activityId}
          onClose={() => setScheduleRow(null)}
          getContainer={popupContainer}
        />

        <MachineryTaskBreakdownDrawer
          open={!!breakdownRow}
          projectId={projectId}
          row={breakdownRow}
          phaseId={phaseId}
          activityId={activityId}
          onClose={() => setBreakdownRow(null)}
          getContainer={popupContainer}
        />
        </ConfigProvider>
        </div>
      </Content>
    </>
  );
};

export default MachineryCostAnalysisReport;
